import React from 'react';
import { PiggyBank } from 'lucide-react';

/**
 * Компонент секции досрочного погашения
 */
const EarlyRepaymentSection = ({ formData, handleInputChange, errors, earlyRepaymentPeriods }) => {
  return (
    <fieldset className="mb-8 border-b border-gray-200 dark:border-gray-700 pb-8">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <legend className="text-xl font-medium text-gray-800 dark:text-gray-200 flex items-center gap-2">
          <PiggyBank className="w-5 h-5 text-green-500" />
          Досрочное погашение
        </legend>
        <div className="flex items-center">
          <label htmlFor="includeEarlyRepayment" className="mr-2 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer select-none">
            Учитывать?
          </label>
          <input
            type="checkbox" id="includeEarlyRepayment" name="includeEarlyRepayment"
            checked={formData.includeEarlyRepayment} onChange={handleInputChange}
            className="form-checkbox"
          />
        </div>
      </div>

      {formData.includeEarlyRepayment && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5 bg-gray-50 dark:bg-gray-800/50 p-4 rounded-md border dark:border-gray-700">
          {/* Сумма досрочного погашения */}
          <div>
            <label htmlFor="earlyRepaymentAmount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Сумма</label>
            <div className="relative rounded-md shadow-sm">
              <input
                type="text" id="earlyRepaymentAmount" name="earlyRepaymentAmount" inputMode="decimal"
                value={formData.earlyRepaymentAmount} onChange={handleInputChange}
                className={`form-input pl-4 pr-10 ${errors.earlyRepaymentAmount ? 'input-error' : ''}`}
                placeholder="100 000" required={formData.includeEarlyRepayment}
                aria-invalid={!!errors.earlyRepaymentAmount} aria-describedby="earlyRepaymentAmount-error"
              />
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <span className="text-gray-500 dark:text-gray-400 sm:text-sm">₽</span>
              </div>
            </div>
            {errors.earlyRepaymentAmount && (
              <p id="earlyRepaymentAmount-error" className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
                {errors.earlyRepaymentAmount}
              </p>
            )}
          </div>

          {/* Периодичность досрочного погашения */}
          <div>
            <label htmlFor="earlyRepaymentPeriod" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Периодичность</label>
            <select
              id="earlyRepaymentPeriod" name="earlyRepaymentPeriod" value={formData.earlyRepaymentPeriod} onChange={handleInputChange}
              className={`form-select ${errors.earlyRepaymentPeriod ? 'input-error' : ''}`} required={formData.includeEarlyRepayment}
              aria-invalid={!!errors.earlyRepaymentPeriod} aria-describedby="earlyRepaymentPeriod-error"
            >
              {earlyRepaymentPeriods.map(period => (
                <option key={period.id} value={period.id}>{period.name}</option>
              ))}
            </select>
            {errors.earlyRepaymentPeriod && (
              <p id="earlyRepaymentPeriod-error" className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
                {errors.earlyRepaymentPeriod}
              </p>
            )}
          </div>

          {/* Начало досрочного погашения */}
          <div>
            <label htmlFor="earlyRepaymentStartMonth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Начиная с месяца</label>
            <input
              type="text" id="earlyRepaymentStartMonth" name="earlyRepaymentStartMonth" inputMode="numeric"
              value={formData.earlyRepaymentStartMonth} onChange={handleInputChange}
              className={`form-input ${errors.earlyRepaymentStartMonth ? 'input-error' : ''}`}
              placeholder="12" required={formData.includeEarlyRepayment}
              aria-invalid={!!errors.earlyRepaymentStartMonth} aria-describedby="earlyRepaymentStartMonth-error"
            />
            {errors.earlyRepaymentStartMonth && (
              <p id="earlyRepaymentStartMonth-error" className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
                {errors.earlyRepaymentStartMonth}
              </p>
            )}
          </div>

          {/* Цель досрочного погашения */}
          <div className="md:col-span-2 lg:col-span-3">
            <fieldset>
              <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Цель досрочного погашения</legend>
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                <div className="flex items-center">
                  <input
                    id="earlyRepaymentTypeReduction" type="radio" name="earlyRepaymentType"
                    value="reduction" checked={formData.earlyRepaymentType === 'reduction'}
                    onChange={handleInputChange} className="form-radio"
                    required={formData.includeEarlyRepayment}
                    aria-describedby="earlyRepaymentType-error"
                  />
                  <label htmlFor="earlyRepaymentTypeReduction" className="ml-2 block text-sm text-gray-900 dark:text-gray-300 cursor-pointer">
                    Уменьшение срока
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    id="earlyRepaymentTypePayment" type="radio" name="earlyRepaymentType"
                    value="payment" checked={formData.earlyRepaymentType === 'payment'}
                    onChange={handleInputChange} className="form-radio"
                    required={formData.includeEarlyRepayment}
                    aria-describedby="earlyRepaymentType-error"
                  />
                  <label htmlFor="earlyRepaymentTypePayment" className="ml-2 block text-sm text-gray-900 dark:text-gray-300 cursor-pointer">
                    Уменьшение платежа
                  </label>
                </div>
              </div>
            </fieldset>
            {errors.earlyRepaymentType && (
              <p id="earlyRepaymentType-error" className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
                {errors.earlyRepaymentType}
              </p>
            )}
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Уменьшение срока обычно приводит к большей экономии на процентах.
            </p>
          </div>
        </div>
      )}
    </fieldset>
  );
};

export default EarlyRepaymentSection;